/**
 * NumFree React App with JWT Authentication
 */

import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, Link } from 'react-router-dom';
import { getUser, logout, initAuth, resetAuthInit, isAuthenticated, authFetch } from './auth';
import TwilioDialer from './components/TwilioDialer';
import UserProfile from './components/UserProfile';
import VerificationPage from './components/Verification/VerificationPage';
import EsimDashboard from './components/eSIM/EsimDashboard';
import AdminDashboard from './components/Admin/AdminDashboard';
import SMSInterface from './components/SMS/SMSInterface';
import PhoneNumberManager from './components/PhoneNumbers/PhoneNumberManager';
import CallHistoryPanel from './components/CallHistory/CallHistoryPanel';
import ContactsPage from './components/Contacts/ContactsPage';
import PortingWizard from './components/Porting/PortingWizard';
import HelpPage from './components/Help/HelpPage';
import VPNDashboard from './components/VPN/VPNDashboard';
import AIAgentDashboard from './components/AIAgent/AIAgentDashboard';
import AIAgentV2 from './components/AIAgent/AIAgentV2';
import CalendarPage from './components/Calendar/CalendarPage';
import NotesPage from './components/Notes/NotesPage';
import TasksPage from './components/Tasks/TasksPage';
import BookingPage from './components/Booking/BookingPage';
import GuestBookingPage from './components/Booking/GuestBookingPage';
import MeetingPage from './components/Meeting/MeetingPage';
import MarketplacePage from './components/Marketplace/MarketplacePage';
import LinkTimePage from './components/LinkTime/LinkTimePage';
import LinkTimeJoinPage from './components/LinkTime/LinkTimeJoinPage';
import LinkTimeLandingPage from './components/LinkTime/LinkTimeLandingPage';
import SharedChatView from './components/SharedChat/SharedChatView';
import PrerequisiteGate from './components/PrerequisiteGate/PrerequisiteGate';
import useSessionPresenceWebSocket from './utils/useSessionPresenceWebSocket';
import './App.css';
import LoginPage from './LoginPage';
import SignupPage from './SignupPage';

const isLinkTimeHost = () => {
  if (typeof window === 'undefined') return false;
  return window.location.hostname === 'link.numfree.com';
};

function App() {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    // SECURITY: auth lives in httpOnly cookies; we can't read them from JS.
    // initAuth() does GET /api/me — cookie rides along — to learn who we are.
    const checkAuth = async () => {
      const u = await initAuth();
      if (u) {
        setUser(u);
        try {
          const res = await authFetch('/auth0/app_profile/');
          if (res.ok) setUserProfile(await res.json());
        } catch (e) {}
      }
      setAuthChecked(true);
    };
    checkAuth();
  }, []);

  const loadUserProfile = async () => {
    try {
      const res = await authFetch('/auth0/app_profile/');
      if (res.ok) setUserProfile(await res.json());
    } catch (e) {}
  };

  const handleLogout = () => {
    logout();
    setUser(null);
    setUserProfile(null);
  };

  const handleLoginSuccess = () => {
    setUser(getUser());
    loadUserProfile();
    var params = new URLSearchParams(window.location.search);
    var redirect = params.get('redirect');
    // SECURITY: only follow same-origin relative paths. Reject absolute/external URLs
    // and protocol-relative ("//evil.com") / backslash ("/\evil.com") tricks so a
    // crafted ?redirect= can't send a freshly authenticated user off-site (open redirect).
    if (redirect && /^\/(?![/\\])/.test(redirect)) {
      window.location.href = redirect;
    }
  };

  const onCallStatusChange = (status, isActive, duration) => {
    console.log('Call status:', status, 'Active:', isActive, 'Duration:', duration);
  };

  if (!authChecked) {
    return <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100dvh',color:'#8892b0',fontSize:14}}>Loading...</div>;
  }

  // Bare shared-chat viewer — accessible to both anon and authenticated users
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/s/')) {
    return (
      <Router>
        <Routes>
          <Route path="/s/:shortcode" element={<SharedChatView />} />
        </Routes>
      </Router>
    );
  }

  return (
    <Router>
      {!user ? (
        <PublicApp onLoginSuccess={handleLoginSuccess} />
      ) : (
        <AuthenticatedApp user={user} userProfile={userProfile} onLogout={handleLogout} onCallStatusChange={onCallStatusChange} onProfileUpdate={loadUserProfile} />
      )}
    </Router>
  );
}

function PublicApp({ onLoginSuccess }) {
  const location = useLocation();

  // LinkTime guest pages render bare (no landing chrome) regardless of host
  if (location.pathname.startsWith('/link/') || location.pathname.startsWith('/meeting/')) {
    return (
      <Routes>
        <Route path="/link/:alias/:inviteCode" element={<LinkTimeJoinPage />} />
        <Route path="/meeting/:meetingToken" element={<MeetingPage />} />
      </Routes>
    );
  }

  if (isLinkTimeHost() && location.pathname === '/') {
    return (
      <Routes>
        <Route path="/" element={<LinkTimeLandingPage />} />
        <Route path="/login" element={<LoginPage onLoginSuccess={onLoginSuccess} />} />
        <Route path="/signup" element={<SignupPage onSignupSuccess={onLoginSuccess} />} />
        <Route path="/forgot" element={<ForgotPasswordPage />} />
        <Route path="/reset/:token" element={<ResetPasswordPage />} />
        <Route path="/meet/:username" element={<BookingPage />} />
        <Route path="/booking/guest/:guestToken" element={<GuestBookingPage />} />
        <Route path="/meeting/:meetingToken" element={<MeetingPage />} />
        <Route path="/linktime" element={<LegacyLinkTimeJoinRedirect />} />
        <Route path="/link/:alias/:inviteCode" element={<LinkTimeJoinPage />} />
        <Route path="/:alias/:inviteCode" element={<LinkTimeJoinPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  return (
    <div className="App">
      <header className="app-header">
        <div className="header-content">
          <Link to="/login" className="logo" aria-label="NumFree">
            <img src="/assets/dynamic/NumFreeSymbol.png" className="logo-symbol" alt="" />
          </Link>
        </div>
      </header>
      <main className="app-main">
        <Routes>
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/help" element={<HelpPage />} />
          <Route path="/login" element={<LoginPage onLoginSuccess={onLoginSuccess} />} />
          <Route path="/signup" element={<SignupPage onSignupSuccess={onLoginSuccess} />} />
          <Route path="/forgot" element={<ForgotPasswordPage />} />
          <Route path="/reset/:token" element={<ResetPasswordPage />} />
          <Route path="/meet/:username" element={<BookingPage />} />
          <Route path="/booking/guest/:guestToken" element={<GuestBookingPage />} />
          <Route path="/meeting/:meetingToken" element={<MeetingPage />} />
          <Route path="/linktime" element={<LegacyLinkTimeJoinRedirect />} />
          <Route path="/link/:alias/:inviteCode" element={<LinkTimeJoinPage />} />
          <Route path="/:alias/:inviteCode" element={isLinkTimeHost() ? <LinkTimeJoinPage /> : <Navigate to="/login" replace />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </main>
      <footer className="app-footer">
        <p>&copy; 2025 - 2026 NumFree Inc.</p>
      </footer>
    </div>
  );
}

// Authenticated app: a bare share link (/<alias>/<inviteCode>, e.g. from link.numfree.com) must
// reach the meeting just like the public app does. Forward it to the canonical /link/... route,
// which is already handled AND flagged as a meeting route (full-screen layout).
function BareLinkRedirect() {
  const location = useLocation();
  return <Navigate to={'/link' + location.pathname + location.search} replace />;
}

function LegacyLinkTimeJoinRedirect() {
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const meetingId = params.get('join');

    if (!meetingId) {
      navigate('/login', { replace: true });
      return;
    }

    if (!isAuthenticated()) {
      navigate('/login?redirect=' + encodeURIComponent('/linktime?join=' + meetingId), { replace: true });
      return;
    }

    var redirectPath = '/link/' + encodeURIComponent(meetingId);

    function resolvePublic() {
      return fetch('/api/linktime/meetings-public/' + encodeURIComponent(meetingId))
        .then(function(res) {
          if (!res.ok) throw new Error('Meeting not found');
          return res.json();
        })
        .then(function(data) {
          if (!data.room_alias || !data.meeting_code) throw new Error('Meeting not found');
          redirectPath = '/link/' + encodeURIComponent(data.room_alias) + '/' + encodeURIComponent(data.meeting_code);
        });
    }

    function tryAuthResolve() {
      if (!isAuthenticated()) return Promise.resolve(null); // not logged in, skip auth
      return authFetch('/api/linktime/meetings/' + encodeURIComponent(meetingId))
        .then(function(res) {
          if (!res.ok) return null; // not authorized as host, fall through
          return res.json();
        })
        .then(function(data) {
          if (data && data.room_alias) {
            redirectPath = '/link/' + encodeURIComponent(data.room_alias) + '/' + encodeURIComponent(data.meeting_code) + '?host=1';
            // Store meeting_code for later lookup via meetingId
            if (data.meeting_code) {
              try { sessionStorage.setItem('lt_host_' + meetingId, data.meeting_code); } catch(e) {}
            }
            return true;
          }
          return null;
        });
    }

    tryAuthResolve()
      .then(function(hostResolved) {
        if (!hostResolved) return resolvePublic();
      })
      .then(function() {
        navigate(redirectPath, { replace: true });
      })
      .catch(function(err) { setError(err.message || 'Meeting not found'); });
  }, [location.search, navigate]);

  return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100dvh',color:'#1E203F',fontSize:14}}>
      {error || 'Opening LinkTime...'}
    </div>
  );
}

// Authenticated layout with sidebar
function AuthenticatedApp({ user, userProfile, onLogout, onCallStatusChange, onProfileUpdate }) {
  const location = useLocation();
  const currentPath = location.pathname;
  const isSmsRoute = currentPath === '/sms';
  const isAIAgentRoute = currentPath === '/ai-agent' || currentPath === '/ai-agent-v2';
  const isNotesRoute = currentPath === '/notes';
  const isMeetingRoute = currentPath.startsWith('/meeting/') || currentPath.startsWith('/link/');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const closeSidebar = () => setSidebarOpen(false);
  const userInitial = (user?.email || 'U')[0].toUpperCase();
  const accountType = userProfile?.ai_tier || userProfile?.account_type || userProfile?.plan || userProfile?.numfreeplan || 'free';
  const routingPreference = userProfile?.ai_routing_preference || 'openrouter';
  const [apiVersion, setApiVersion] = useState(null);

  useEffect(function() {
    fetch('/api/version')
      .then(function(r) { return r.json(); })
      .then(function(v) { setApiVersion(v); })
      .catch(function() {});
  }, []);

  // Maintain session presence even in full-screen meeting routes
  useSessionPresenceWebSocket(!!user, 'Authenticated');

  const pageTitle = {
    '/dialer': 'Dialer', '/sms': 'Messages', '/call-history': 'Calls + VMs',
    '/numbers': 'My Numbers', '/porting': 'Port Number',
    '/contacts': 'Contacts', '/calendar': 'Calendar', '/notes': 'Notes', '/tasks': 'Tasks',
    '/esim': 'eSIM Data', '/vpn': 'VPN', '/ai-agent': 'AI Assistant', '/ai-agent-v2': 'AI Assistant V2',
    '/help': 'Help', '/profile': 'Profile', '/admin': 'Admin', '/linktime': 'LinkTime', '/verification': 'Verification', '/marketplace': 'Marketplace'
  }[currentPath] || '';

  if (isMeetingRoute) {
    return (
      <div className="App">
        <Routes>
          <Route path="/meeting/:meetingToken" element={<MeetingPage />} />
          <Route path="/link/:alias/:inviteCode" element={<LinkTimeJoinPage />} />
        </Routes>
      </div>
    );
  }

  const aiAgentItem = {
    path: '/ai-agent', label: 'AI Assistant',
    icon: <svg viewBox="0 0 24 24" width="22" height="22" fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5c-2 0-3.5 1-4.5 2.5C6.5 9 6 10.5 6 12c0 2 1 3.5 2.5 4.5" stroke="#e65100" strokeWidth="2"/>
      <path d="M12 5c2 0 3.5 1 4.5 2.5C17.5 9 18 10.5 18 12c0 2-1 3.5-2.5 4.5" stroke="#e65100" strokeWidth="2"/>
      <path d="M9 16.5c.8.8 1.8 1.5 3 1.5s2.2-.7 3-1.5" stroke="#e65100" strokeWidth="2"/>
      <circle cx="9.5" cy="10" r="1" fill="#e65100"/><circle cx="14.5" cy="10" r="1" fill="#e65100"/>
      <path d="M6 7.5C4.5 6 3 5.5 2 6" stroke="#e65100" strokeWidth="1.5"/>
      <path d="M18 7.5C19.5 6 21 5.5 22 6" stroke="#e65100" strokeWidth="1.5"/>
      <path d="M2 6c-.5 1 0 2.5 1 3.5" stroke="#e65100" strokeWidth="1.5"/>
      <path d="M22 6c.5 1 0 2.5-1 3.5" stroke="#e65100" strokeWidth="1.5"/>
    </svg>
  };

  const navGroups = [
    { label: 'Communication', items: [
      { path: '/linktime', label: 'LinkTime', icon: <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14v-4z"/><rect x="3" y="6" width="12" height="12" rx="2" ry="2"/></svg> },
      { path: '/dialer', label: 'Dialer', icon: <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg> },
      { path: '/sms', label: 'Messages', icon: <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg> },
      { path: '/call-history', label: 'Calls + VMs', icon: <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg> },
    ]},
    { label: 'Numbers', items: [
      { path: '/numbers', label: 'My Numbers', icon: <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 4h4l2 5-2.5 1.5a11 11 0 005 5L15 13l5 2v4a2 2 0 01-2 2A16 16 0 013 6a2 2 0 012-2"/><line x1="15" y1="3" x2="15" y2="9"/><line x1="12" y1="6" x2="18" y2="6"/></svg> },
      { path: '/porting', label: 'Port Number', icon: <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v10m0 0l-4-4m4 4l4-4"/><path d="M20 17.58A11 11 0 113.41 8"/></svg> },
    ]},
    { label: 'Productivity', items: [
      { path: '/contacts', label: 'Contacts', icon: <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg> },
      { path: '/calendar', label: 'Calendar', icon: <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg> },
      { path: '/notes', label: 'Notes', icon: <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg> },
      { path: '/tasks', label: 'Tasks', icon: <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg> },
    ]},
    { label: 'Services', items: [
      { path: '/esim', label: 'eSIM Data', icon: <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg> },
      { path: '/vpn', label: 'VPN', icon: <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg> },
    ]},
  ];

  const bottomItems = [
    { path: '/help', label: 'Help', icon: <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg> },
    { path: '/verification', label: 'Verification', icon: <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg> },
    { path: '/profile', label: 'Profile', icon: <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg> },
  ];

  if (user?.email?.toLowerCase() === 'nfodor@mac.com') {
    bottomItems.push({ path: '/admin', label: 'Admin', icon: <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg> });
  }

  return (
    <div className="App app-layout">
      <button data-testid="sidebar-toggle" className="sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
        <span style={{fontWeight:800,fontSize:18,letterSpacing:'-0.03em',color:'#fff',lineHeight:1}}><span style={{color:'#e65100'}}>#</span></span>
      </button>
      <div className={`sidebar-overlay ${sidebarOpen ? 'visible' : ''}`} onClick={closeSidebar} />
      <aside className={`app-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-logo">
          <Link to="/dialer" className="sidebar-logo-text" onClick={closeSidebar}>
            <h1>&nbsp;&nbsp;&nbsp;&nbsp;</h1>
          </Link>
        </div>
        <div className="sidebar-hero">
          <Link data-testid="nav-ai-assistant" to={aiAgentItem.path} className={`nav-item nav-item-hero ${currentPath === aiAgentItem.path ? 'active' : ''}`} onClick={closeSidebar}>
            {aiAgentItem.icon}
            {aiAgentItem.label}
          </Link>
          <Link to="/marketplace" className={`nav-item ${currentPath === '/marketplace' ? 'active' : ''}`} onClick={closeSidebar} style={{marginLeft: 8, fontSize: 13}}>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>
            Marketplace
          </Link>
        </div>
        <nav className="sidebar-nav">
          {navGroups.map(group => (
            <div key={group.label} className="nav-group">
              <div className="nav-group-label">{group.label}</div>
              {group.items.map(item => (
                <Link key={item.path} data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`} to={item.path} className={`nav-item ${currentPath === item.path ? 'active' : ''}`} onClick={closeSidebar}>
                  {item.icon}
                  {item.label}
                </Link>
              ))}
            </div>
          ))}
          <div className="nav-group nav-group-bottom">
            {bottomItems.map(item => (
              <Link key={item.path} to={item.path} className={`nav-item ${currentPath === item.path ? 'active' : ''}`} onClick={closeSidebar}>
                {item.icon}
                {item.label}
              </Link>
            ))}
          </div>
        </nav>
        <div className="sidebar-user">
          <div className="sidebar-avatar">{userInitial}</div>
          <div className="sidebar-user-info">
            <div className="sidebar-user-email">{user?.email || 'User'}</div>
            <div className="sidebar-user-tier">{String(accountType).toUpperCase()} · {routingPreference === 'direct_google' ? 'Direct Google' : 'OpenRouter'}</div>
          </div>
          <button data-testid="logout-btn" className="sidebar-logout" onClick={onLogout} title="Logout">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
          </button>
        </div>
        {process.env.REACT_APP_VERSION && <div className="sidebar-version">v{process.env.REACT_APP_VERSION}{apiVersion && <><br/>API {apiVersion.displayVersion}</>}</div>}
      </aside>
      <div className={`app-main-content ${isSmsRoute ? 'sms-route-active' : ''} ${isAIAgentRoute ? 'ai-agent-route-active' : ''} ${isNotesRoute ? 'notes-route-active' : ''}`.trim()}>
        <div className="app-topbar"><h1 className="topbar-title">{pageTitle}</h1></div>
        <main className={`app-main ${isSmsRoute ? 'sms-route-active' : ''} ${isAIAgentRoute ? 'ai-agent-route-active' : ''} ${isNotesRoute ? 'notes-route-active' : ''}`.trim()}>
          <Routes>
            <Route path="/" element={<Navigate to="/ai-agent" replace />} />
            <Route path="/login" element={<Navigate to="/ai-agent" replace />} />
            <Route path="/signup" element={<Navigate to="/ai-agent" replace />} />
            <Route path="/dialer" element={<DialerPage user={userProfile} onCallStatusChange={onCallStatusChange} />} />
            <Route path="/numbers" element={<PhoneNumbersPage user={userProfile} onProfileUpdate={onProfileUpdate} />} />
            <Route path="/porting" element={<PortingPage user={userProfile} />} />
            <Route path="/sms" element={<SMSPage user={userProfile} />} />
            <Route path="/call-history" element={<RecentsPage user={userProfile} />} />
            <Route path="/contacts" element={<ContactsPage />} />
            <Route path="/profile" element={<ProfilePage user={userProfile} onProfileUpdate={onProfileUpdate} />} />
            <Route path="/verification" element={<VerificationPage user={userProfile} />} />
            <Route path="/esim" element={<EsimPage user={userProfile} />} />
            <Route path="/vpn" element={<VPNPage />} />
            <Route path="/calendar" element={<CalendarPage user={userProfile} />} />
            <Route path="/notes" element={<NotesPage />} />
            <Route path="/tasks" element={<TasksPage />} />
            <Route path="/ai-agent" element={<AIAgentPage user={userProfile} />} />
            <Route path="/ai-agent-v2" element={<AIAgentV2Page user={userProfile} />} />
            <Route path="/linktime" element={<LinkTimePage user={userProfile} />} />
            <Route path="/link/:alias/:inviteCode" element={<LinkTimeJoinPage />} />
            <Route path="/marketplace" element={<MarketplacePage />} />
            <Route path="/meet/:username" element={<BookingPage />} />
            <Route path="/booking/guest/:guestToken" element={<GuestBookingPage />} />
            <Route path="/meeting/:meetingToken" element={<MeetingPage />} />
            <Route path="/admin" element={<AdminPage user={userProfile} />} />
            {/* Bare share link (link.numfree.com/<alias>/<code>) → canonical /link/ meeting route. Last, so it only catches unmatched 2-segment paths. */}
            <Route path="/:alias/:inviteCode" element={<BareLinkRedirect />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

// Login Page Component
const DialerPage = ({ user, onCallStatusChange }) => (
  <div className="dialer-page">
    <PrerequisiteGate requires={['hasNumber']} user={user} featureLabel="Dialer">
      <div className="dialer-content">
        <div className="dialer-section">
          <TwilioDialer
            user={user}
            onCallStatusChange={onCallStatusChange}
          />
        </div>

        <div className="call-history-section">
          <CallHistoryPanel userPhoneNumbers={user?.freenumbers || []} />
        </div>
      </div>
    </PrerequisiteGate>
  </div>
);

// Phone Numbers Page Component
const PhoneNumbersPage = ({ user, onProfileUpdate }) => (
  <div className="phone-numbers-page">
    <PhoneNumberManager user={user} onPurchaseDone={onProfileUpdate} />
  </div>
);

// SMS Page Component
const SMSPage = ({ user }) => (
  <div className="sms-page">
    <PrerequisiteGate requires={['hasNumber']} user={user} featureLabel="Messages">
      <SMSInterface userPhoneNumbers={user?.freenumbers || []} />
    </PrerequisiteGate>
  </div>
);

// Profile Page Component
const ProfilePage = ({ user, onProfileUpdate }) => (
  <div className="profile-page">
    <div className="page-header">
      <h2>User Profile</h2>
    </div>

    <UserProfile
      user={user}
      onUpdate={onProfileUpdate}
    />
  </div>
);

// Porting Page Component
const PortingPage = ({ user }) => (
  <div className="porting-page">
    <PortingWizard user={user} />
  </div>
);

// eSIM Page Component (user view — browse & buy)
const EsimPage = ({ user }) => (
  <div className="esim-page">
    <EsimDashboard user={user} />
  </div>
);

// Admin Page (user management + eSIM admin as tabs)
const ADMIN_EMAIL = 'nfodor@mac.com';
const AdminPage = ({ user }) => {
  // Still loading profile — show spinner instead of redirecting
  if (!user) {
    return (
      <div className="admin-page" style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
        <div className="loading-spinner" />
      </div>
    );
  }
  if (!user.email || user.email.toLowerCase() !== ADMIN_EMAIL) {
    return <Navigate to="/ai-agent" replace />;
  }
  return (
    <div className="admin-page">
      <AdminDashboard user={user} />
    </div>
  );
};

// Missing page wrappers
const RecentsPage = ({ user }) => (
  <div className="recents-page">
    <PrerequisiteGate requires={['hasNumber']} user={user} featureLabel="Calls + VMs">
      <CallHistoryPanel userPhoneNumbers={user?.freenumbers || []} />
    </PrerequisiteGate>
  </div>
);
const VPNPage = () => (
  <div className="vpn-page"><VPNDashboard /></div>
);
const AIAgentPage = ({ user }) => (
  <div className="ai-agent-page"><AIAgentDashboard user={user} /></div>
);
const AIAgentV2Page = ({ user }) => (
  <div className="ai-agent-page"><AIAgentV2 user={user} /></div>
);
const ForgotPasswordPage = () => <div className="auth-page"><p>Forgot password — check your email</p></div>;
const ResetPasswordPage = () => <div className="auth-page"><p>Reset password</p></div>;

export default App;
// rebuild 1774232146
